import { useId, useRef, useState } from 'react'
import { Button } from '../Button'
import { Text } from '../Text'
import { DISTRIBUTION_TIERS, getRatingColor } from './shared'

export interface OverallDistributionParticipant {
	userId: string
	firstName: string | null
	lastName: string | null
	jobTitle?: string | null
	avgScore: number | null
	cycleCount: number
	highestRatedDealName: string | null
	highestRatedDealScore: number | null
	lowestRatedDealName: string | null
	lowestRating: number | null
}

export interface OverallDistributionBucketData {
	total: number
	participants: OverallDistributionParticipant[]
}

type OverallDistributionBucket = { label: string; count: number; percentage: number }

const MAX_BUCKET_VISIBLE = 3

function ArrowUpRightIcon({ size = 16, className }: Readonly<{ size?: number; className?: string }>) {
	return (
		<svg
			width={size}
			height={size}
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth='2.2'
			strokeLinecap='round'
			strokeLinejoin='round'
			className={className}
			aria-hidden='true'>
			<path d='M7 17 17 7' />
			<path d='M8 7h9v9' />
		</svg>
	)
}

function ParticipantTooltipCard({
	participant,
	scaleMax,
	onViewProfile,
	onViewReport,
}: Readonly<{
	participant: OverallDistributionParticipant
	scaleMax: number
	onViewProfile: () => void
	onViewReport?: () => void
}>) {
	const name = [participant.firstName, participant.lastName].filter(Boolean).join(' ')
	const initials = [participant.firstName?.[0], participant.lastName?.[0]].filter(Boolean).join('').toUpperCase()

	return (
		<div className='rounded-xl border border-neutral-200 bg-white shadow-xl p-4 flex flex-col gap-3 text-left'>
			<div className='flex items-center gap-3'>
				<div className='h-10 w-10 rounded-full bg-neutral-100 flex items-center justify-center text-sm font-semibold text-neutral-600 shrink-0'>
					{initials}
				</div>
				<div className='min-w-0'>
					<p className='text-sm font-semibold text-neutral-900 leading-tight truncate'>{name}</p>
					{participant.jobTitle && <p className='text-xs text-neutral-500 truncate'>{participant.jobTitle}</p>}
				</div>
			</div>

			<div className='flex gap-6'>
				<div>
					<p className='text-xs text-neutral-400 mb-0.5'>Average Rating</p>
					{participant.avgScore == null ? (
						<span className='text-xs text-neutral-400'>—</span>
					) : (
						<span
							className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getRatingColor(participant.avgScore, scaleMax)}`}>
							{participant.avgScore.toFixed(2)}
						</span>
					)}
				</div>
				<div>
					<p className='text-xs text-neutral-400 mb-0.5'>Deals Involved</p>
					<p className='text-sm font-medium text-neutral-800'>{participant.cycleCount} Deals</p>
				</div>
			</div>

			{participant.highestRatedDealName && (
				<div>
					<p className='text-xs text-neutral-400 mb-0.5'>Highest Rated Deal</p>
					<div className='flex items-center gap-2'>
						{participant.highestRatedDealScore != null && (
							<span
								className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium shrink-0 ${getRatingColor(participant.highestRatedDealScore, scaleMax)}`}>
								{participant.highestRatedDealScore.toFixed(2)}
							</span>
						)}
						<span className='text-xs text-neutral-700 truncate'>{participant.highestRatedDealName}</span>
					</div>
				</div>
			)}

			{participant.lowestRatedDealName && (
				<div>
					<p className='text-xs text-neutral-400 mb-0.5'>Lowest Rated Deal</p>
					<div className='flex items-center gap-2'>
						{participant.lowestRating != null && (
							<span
								className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium shrink-0 ${getRatingColor(participant.lowestRating, scaleMax)}`}>
								{participant.lowestRating.toFixed(2)}
							</span>
						)}
						<span className='text-xs text-neutral-700 truncate'>{participant.lowestRatedDealName}</span>
					</div>
				</div>
			)}

			<div className='flex items-center gap-2'>
				<Button variant='secondary' size='small' onClick={onViewProfile}>
					View Profile
				</Button>
				{onViewReport && (
					<button
						type='button'
						onClick={onViewReport}
						className='inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700'>
						Report
						<ArrowUpRightIcon size={16} className='text-white' />
					</button>
				)}
			</div>
		</div>
	)
}

export function OverallDistributionView({
	distribution,
	distributionBuckets,
	onViewProfile,
	onViewReport,
	scaleMax = 10,
}: Readonly<{
	distribution: OverallDistributionBucket[]
	distributionBuckets: OverallDistributionBucketData[] | null
	onViewProfile: (userId: string) => void
	onViewReport?: (userId: string) => void
	scaleMax?: number
}>) {
	const clipId = useId()
	const [hoveredBar, setHoveredBar] = useState<number | null>(null)
	const [hoveredParticipant, setHoveredParticipant] = useState<OverallDistributionParticipant | null>(null)
	const [tooltipSide, setTooltipSide] = useState<'left' | 'right'>('right')
	const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const handleParticipantEnter = (p: OverallDistributionParticipant, el?: HTMLElement) => {
		if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
		if (el) {
			const rect = el.getBoundingClientRect()
			setTooltipSide(rect.right + 248 > window.innerWidth ? 'left' : 'right')
		}
		setHoveredParticipant(p)
	}

	const handleParticipantLeave = () => {
		leaveTimeoutRef.current = setTimeout(() => setHoveredParticipant(null), 150)
	}

	if (distribution.length === 0) return null

	const maxCount = Math.max(...distribution.map((b) => b.count), 1)
	const total = distribution.reduce((sum, b) => sum + b.count, 0)
	const n = distribution.length

	const BAR_HEIGHT_MAX = 82
	const pts = distribution.map((b, i) => ({
		x: ((i + 0.5) / n) * 100,
		y: Math.max(2, (1 - b.count / maxCount) * BAR_HEIGHT_MAX - 8),
	}))
	let curvePath = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`
	for (let i = 0; i < pts.length - 1; i++) {
		const p0 = pts[Math.max(0, i - 1)]
		const p1 = pts[i]
		const p2 = pts[i + 1]
		const p3 = pts[Math.min(pts.length - 1, i + 2)]
		const cp1x = p1.x + (p2.x - p0.x) / 6
		const cp1y = p1.y + (p2.y - p0.y) / 6
		const cp2x = p2.x - (p3.x - p1.x) / 6
		const cp2y = p2.y - (p3.y - p1.y) / 6
		curvePath += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`
	}

	const dimmed = (i: number) => hoveredBar !== null && hoveredBar !== i

	return (
		<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-3 text-left'>
			<Text variant='large' weight='semibold' className='mb-1'>
				Overall Distribution
			</Text>
			{/* Bars + bell curve + floating count labels */}
			<div className='relative flex gap-px h-44 mt-2'>
				<svg
					className='absolute inset-0 w-full h-full pointer-events-none overflow-hidden'
					viewBox='0 0 100 100'
					preserveAspectRatio='none'
					aria-hidden='true'>
					<clipPath id={clipId}>
						<rect x='0' y='0' width='100' height='100' />
					</clipPath>
					<path
						d={curvePath}
						fill='none'
						stroke='#6B9FD4'
						strokeWidth='2'
						vectorEffect='non-scaling-stroke'
						clipPath={`url(#${clipId})`}
					/>
				</svg>
				{distribution.map((b, i) => {
					const tier = DISTRIBUTION_TIERS[i]
					const heightPct = (b.count / maxCount) * BAR_HEIGHT_MAX
					const topPct = 100 - heightPct
					return (
						<button
							key={b.label}
							type='button'
							aria-pressed={hoveredBar === i}
							className='relative flex-1 h-full flex flex-col justify-end cursor-pointer transition-opacity duration-150 p-0 border-0 bg-transparent'
							style={{ opacity: dimmed(i) ? 0.3 : 1 }}
							onClick={() => setHoveredBar(hoveredBar === i ? null : i)}
							onMouseEnter={() => setHoveredBar(i)}
							onMouseLeave={() => setHoveredBar(null)}>
							{b.count > 0 && (
								<span
									className='absolute w-full text-center text-base font-semibold text-neutral-700 pointer-events-none'
									style={{ top: `calc(${topPct}% - 1.75rem)` }}>
									{b.count}
								</span>
							)}
							{b.count > 0 && (
								<div className='w-full rounded-t' style={{ height: `${heightPct}%`, backgroundColor: tier.color }} />
							)}
						</button>
					)
				})}
			</div>

			{/* Percentage + fraction */}
			<div className='flex'>
				{distribution.map((b, i) => (
					<div
						key={b.label}
						className='flex-1 text-center transition-opacity duration-150'
						style={{ opacity: dimmed(i) ? 0.3 : 1 }}>
						<span className='text-xs text-neutral-500'>
							<span className='font-semibold text-neutral-700'>{b.percentage}%</span> {b.count}/{total}
						</span>
					</div>
				))}
			</div>

			{/* Labels */}
			<div className='flex'>
				{distribution.map((b, i) => (
					<div
						key={b.label}
						className='flex-1 text-center transition-opacity duration-150'
						style={{ opacity: dimmed(i) ? 0.3 : 1 }}>
						<span className='text-sm font-medium text-neutral-800 leading-tight'>{b.label}</span>
					</div>
				))}
			</div>

			{/* Participant grid */}
			{distributionBuckets && (
				<div className='border-t border-neutral-100 pt-3 flex'>
					{distribution.map((b, i) => {
						const tier = DISTRIBUTION_TIERS[i]
						const bucket = distributionBuckets[i]
						const overflow = (bucket?.total ?? 0) - MAX_BUCKET_VISIBLE

						return (
							<div
								key={b.label}
								className='flex-1 flex flex-col gap-1 transition-opacity duration-150 border-r border-neutral-100 last:border-r-0 pr-1'
								style={{ opacity: dimmed(i) ? 0.3 : 1 }}>
								{(bucket?.participants ?? []).slice(0, MAX_BUCKET_VISIBLE).map((p) => {
									const fullName = [p.firstName, p.lastName].filter(Boolean).join(' ')
									const initials = [p.firstName?.[0], p.lastName?.[0]].filter(Boolean).join('').toUpperCase()
									const isHovered = hoveredParticipant?.userId === p.userId

									return (
										<div key={p.userId} className='relative border-b border-neutral-100 last:border-b-0'>
											<button
												type='button'
												className='flex w-full items-center gap-1.5 rounded px-1 py-1 text-left cursor-pointer hover:bg-neutral-50 transition-colors'
												onMouseEnter={(e) => handleParticipantEnter(p, e.currentTarget)}
												onMouseLeave={handleParticipantLeave}
												onClick={() => onViewProfile(p.userId)}>
												<div
													className='h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0'
													style={{ backgroundColor: tier.color, color: tier.textColor }}>
													{initials}
												</div>
												<span className='text-xs text-neutral-700 truncate leading-tight'>{fullName}</span>
											</button>
											{isHovered && (
												<div
													aria-hidden='true'
													className={`absolute z-50 top-0 w-60 ${tooltipSide === 'left' ? 'right-full mr-2' : 'left-full ml-2'}`}
													onMouseEnter={() => handleParticipantEnter(p)}
													onMouseLeave={handleParticipantLeave}>
													<ParticipantTooltipCard
														participant={p}
														scaleMax={scaleMax}
														onViewProfile={() => onViewProfile(p.userId)}
														onViewReport={onViewReport ? () => onViewReport(p.userId) : undefined}
													/>
												</div>
											)}
										</div>
									)
								})}
								{overflow > 0 &&
									(() => {
										const hiddenCount = overflow - Math.min(overflow, 3)
										return (
											<div className='flex items-center gap-1.5 px-1 py-1'>
												<div className='flex'>
													{(bucket?.participants ?? [])
														.slice(MAX_BUCKET_VISIBLE, MAX_BUCKET_VISIBLE + 3)
														.map((p, idx) => {
															const initials = [p.firstName?.[0], p.lastName?.[0]]
																.filter(Boolean)
																.join('')
																.toUpperCase()
															return (
																<div
																	key={p.userId}
																	className='h-6 w-6 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-semibold shrink-0'
																	style={{
																		backgroundColor: tier.color,
																		color: tier.textColor,
																		marginLeft: idx === 0 ? 0 : -6,
																	}}>
																	{initials}
																</div>
															)
														})}
												</div>
												{hiddenCount > 0 && <span className='text-xs text-neutral-400'>+{hiddenCount} more</span>}
											</div>
										)
									})()}
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}
